import React, { useState } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import './DeleteSalonModal.css';

const DeleteSalonModal = ({ isOpen, onClose, salon, onSuccess }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const handleClose = () => {
    setStep(1);
    setConfirmationText('');
    setIsDeleting(false);
    onClose();
  };

  const handleDelete = async () => {
    if (step === 1) {
      setStep(2);
      return;
    }

    if (step === 2) {
      if (confirmationText !== 'DELETE') {
        alert(t('salon.pleaseTypeDelete'));
        return;
      }

      setIsDeleting(true);
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/salons/${salon.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        onSuccess();
        handleClose();
      } catch (error) {
        console.error('Error deleting salon:', error);
        alert(t('salon.failedToDeleteSalon'));
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="delete-salon-modal-overlay">
      <div className="delete-salon-modal">
        <div className="delete-salon-modal-header">
          <h3>{t('salon.deleteSalon')}</h3>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <div className="delete-salon-modal-content">
          {step === 1 && (
            <div className="delete-step-1">
              <div className="warning-icon">⚠️</div>
              <h4>{t('salon.deleteSalonConfirm')}</h4>
              <p>{t('salon.deleteSalonWarning')}</p>
              <ul>
                <li>{t('salon.allSalonData')}</li>
                <li>{t('salon.allMemberAccess')}</li>
                <li>{t('salon.allBrandsAndDresses')}</li>
                <li>{t('salon.allAppointmentsAndClients')}</li>
              </ul>
              <p><strong>{t('salon.actionCannotBeUndone')}</strong></p>
            </div>
          )}

          {step === 2 && (
            <div className="delete-step-2">
              <div className="warning-icon">🚨</div>
              <h4>{t('salon.finalConfirmationRequired')}</h4>
              <p>{t('salon.toConfirmDeletion')} <strong>DELETE</strong> {t('salon.inBoxBelow')}</p>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={t('salon.typeDeleteToConfirm')}
                className="confirmation-input"
                autoFocus
              />
              <p className="salon-info">
                {t('salon.deletingSalon')} <strong>{salon.name}</strong>
              </p>
            </div>
          )}
        </div>

        <div className="delete-salon-modal-footer">
          <button 
            className="salon-button secondary" 
            onClick={handleClose}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </button>
          <button 
            className={`salon-button primary ${step === 2 ? 'final-delete' : ''}`}
            onClick={handleDelete}
            disabled={isDeleting || (step === 2 && confirmationText !== 'DELETE')}
          >
            {isDeleting ? t('salon.deleting') : (step === 1 ? t('salon.continue') : t('salon.deleteSalon'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteSalonModal;
